import styled from 'styled-components';
import { Edit24, Confirm, Disconnect } from 'ui/icons';
import { OverflowMenu } from 'ui/actionMenu';
import { spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { AllowedActionMenuElements } from 'ui/actionMenu/types';
import { LeftTooltip } from 'ui/tooltip';
import { disconnectDisabledText, getSendingPlatformForEspAccount } from '../../../utils';
import { RESET_DEFAULT_SENDER_TEXT, SET_DEFAULT_SENDER_TEXT } from '../../../constants';
import { useSendingPlatformsQueryContext, useModalsContext } from '../../../contexts';

export const TEXT = {
  ID: 'account-integrations-actions-menu',
  EDIT_ACTION: {
    LABEL: 'Edit account',
    ID: 'account-integration-edit-action',
  },
  RESET_DEFAULT_ACTION: {
    LABEL: RESET_DEFAULT_SENDER_TEXT,
    ID: 'account-integration-reset-default-action',
  },
  SET_DEFAULT_ACTION: {
    LABEL: SET_DEFAULT_SENDER_TEXT,
    ID: 'account-integration-set-default-action',
  },
  DISCONNECT_ACTION: {
    LABEL: 'Disconnect account',
    ID: 'account-integration-delete-action',
  },
};

const DisabledWrapper = styled.div`
  display: flex;
  gap: ${spacing.half};
  align-items: center;
  cursor: not-allowed;
`;

const DisabledLabel = styled.p`
  text-align: left;
  width: ${pxToRem(206)};
`;

const DisabledText = styled.span`
  text-align: center;
`;

type AccountOverflowMenuProps = {
  espAccountId: number;
};

export const EmailAccountOverflowMenu = ({ espAccountId }: AccountOverflowMenuProps) => {
  const {
    showEditAccountModal,
    showDisconnectAccountModal,
    showSetDefaultSenderModal,
    showResetDefaultSenderModal,
  } = useModalsContext();
  const { selectEspAccount, hasEmailChannels } = useSendingPlatformsQueryContext();
  const espAccount = selectEspAccount(espAccountId);
  if (!espAccount) return null;

  const { isDefault } = espAccount;
  const sendingPlatform = getSendingPlatformForEspAccount(espAccount);
  const isDisconnectDisabled = isDefault || hasEmailChannels(espAccount);

  const options = [
    {
      content: (
        <>
          <Edit24 />
          <span>{TEXT.EDIT_ACTION.LABEL}</span>
        </>
      ),
      id: TEXT.EDIT_ACTION.ID,
      onClick: () => showEditAccountModal(espAccountId, sendingPlatform),
      as: AllowedActionMenuElements.Button,
    },
    {
      content: isDefault ? (
        <>
          <Confirm />
          <span>{TEXT.RESET_DEFAULT_ACTION.LABEL}</span>
        </>
      ) : (
        <>
          <Confirm />
          <span>{TEXT.SET_DEFAULT_ACTION.LABEL}</span>
        </>
      ),
      id: TEXT.SET_DEFAULT_ACTION.ID,
      onClick: () =>
        isDefault
          ? showResetDefaultSenderModal(espAccountId)
          : showSetDefaultSenderModal(espAccountId),
      as: AllowedActionMenuElements.Button,
      appendDivider: true,
    },
    {
      content: isDisconnectDisabled ? (
        <LeftTooltip
          tooltipContent={
            <DisabledText>
              {disconnectDisabledText(isDefault, hasEmailChannels(espAccount))}
            </DisabledText>
          }
        >
          <DisabledWrapper>
            <Disconnect />
            <DisabledLabel>{TEXT.DISCONNECT_ACTION.LABEL}</DisabledLabel>
          </DisabledWrapper>
        </LeftTooltip>
      ) : (
        <>
          <Disconnect />
          <span>{TEXT.DISCONNECT_ACTION.LABEL}</span>
        </>
      ),
      id: TEXT.DISCONNECT_ACTION.ID,
      onClick: () => showDisconnectAccountModal(espAccountId),
      as: AllowedActionMenuElements.Button,
      isDisabled: isDisconnectDisabled,
    },
  ];

  return <OverflowMenu id={TEXT.ID} options={options} />;
};
